import { Component, Host, h, Prop, State, Element } from "@stencil/core";
import { modalName } from "../nb-contact-modal/modal-name";
import { throttle } from "../../utils/throttle";

@Component({
    tag: "ux-iws-nb-side-navigation",
    styleUrl: "nb-side-navigation.scss",
    shadow: true,
})
export class NbSideNavigation {
    @Element() host: HTMLUxIwsNbSideNavigationElement;
    /** The label for the contact button */
    @Prop() contactLabel?: string;
    /** when a contactLink is set, the button will open the link instead of the modal */
    @Prop() contactLink?: string;
    /** The headline for the side navigation */
    @Prop() headline: string;
    @State() chapters: Array<{
        label: string;
        element: HTMLUxIwsNbChapterIntroElement;
    }> = [];
    @State() activeIndex: number = 0;
    @State() isSticky: boolean = false;
    @State() isHovering: boolean = false;

    componentWillLoad() {
        this.chapters = this.getChapters();
        window.addEventListener("scroll", this.onScroll);
    }

    private onScroll = throttle(() => {
        this.updateActiveIndex();
        this.updateSticky();
    }, 100);

    private updateActiveIndex = () => {
        const scrollPosition = window.scrollY;
        const margin = 40;
        const chapterOffsets = this.chapters.map(
            (chapter) =>
                chapter.element.getBoundingClientRect().top + scrollPosition
        );
        const activeIndex = chapterOffsets.findIndex(
            (offset) => offset > scrollPosition + margin
        );
        this.activeIndex = Math.max(0, activeIndex - 1);
    };

    private updateSticky = () => {
        const clientTop = Math.floor(this.host.getBoundingClientRect().top);
        const computedTop = Math.floor(
            parseFloat(getComputedStyle(this.host).top || "0")
        );
        this.isSticky = clientTop <= computedTop;
    };

    private onClickContactButton() {
        if (this.contactLink) {
            return;
        }
        const modal = document.querySelector<HTMLUxModalElement>(
            `ux-modal[name="${modalName}"]`
        );
        if (modal) {
            modal.triggerOpen();
        }
    }

    private getChapters = () => {
        const chapters = Array.from(
            document.querySelectorAll<HTMLUxIwsNbChapterIntroElement>(
                "ux-iws-nb-chapter-intro[chapter]"
            )
        );
        return chapters.map((element) => ({
            label: element.getAttribute("chapter"),
            element,
        }));
    };

    private scrollToChapter = (element: HTMLElement) => {
        element.scrollIntoView({ behavior: "smooth" });
    };

    render() {
        return (
            <Host is-sticky={this.isSticky}>
                <div
                    class="inner"
                    onMouseEnter={() => (this.isHovering = true)}
                    onMouseLeave={() => (this.isHovering = false)}
                >
                    <div
                        inert={this.isSticky && !this.isHovering}
                        aria-hidden={this.isSticky && !this.isHovering}
                    >
                        <h3 class="headline">{this.headline}</h3>
                        <nav>
                            {this.chapters.map((chapter, index) => (
                                <div key={chapter.label}>
                                    <a
                                        class={{
                                            chapter: true,
                                            "chapter--active":
                                                this.activeIndex === index,
                                        }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            this.scrollToChapter(
                                                chapter.element
                                            );
                                        }}
                                        href="#"
                                    >
                                        {chapter.label}
                                    </a>
                                </div>
                            ))}
                        </nav>
                        <ux-button
                            class="contact-button"
                            variant="primary"
                            size={
                                this.isSticky && !this.isHovering
                                    ? "icon"
                                    : "default"
                            }
                            icon-placement="end"
                            href={this.contactLink}
                            target="_blank"
                            onClick={() => this.onClickContactButton()}
                        >
                            <ux-icon slot="icon" name="mail" weight="bold" />
                            <span class="contact-label">
                                {this.contactLabel}
                            </span>
                        </ux-button>
                    </div>
                </div>
            </Host>
        );
    }
}
